import React, { useMemo, useState } from 'react';
import {
  MacrosContainer,
  MacroPill,
  MacroAmount,
  MacroLabel,
  NutritionDetailBtn,
  NutritionDetails,
  NutritionItem,
  NutritionLabel,
  NutritionSectionTitle,
  NutritionValue,
} from './FoodMacros.styled.jsx';
import { LEGACY_FOOD_MACRO_KEY_MAP, OFF_NUTRIENT_MAP } from '../../config/openFoodFactsNutrients';

const SPECIAL_MACRO_CONFIG = Object.freeze({
  calories: { key: 'calories', label: 'Calories', dataKey: 'calories', unit: 'kcal' },
});

const formatNumber = (value) => {
  const absValue = Math.abs(value);
  const maximumFractionDigits = Number.isInteger(value)
    ? 0
    : absValue < 0.01
      ? 4
      : absValue < 0.1
        ? 3
        : absValue < 1
          ? 2
          : absValue < 10
            ? 1
            : 1;

  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
};

const FoodMacros = ({
  macronutrients,
  mainMacros = [],
  displayedMacros = [],
  hideUnavailableMacros = false,
  renderMainMacros = true,
  expanded: controlledExpanded,
  onToggle,
  showToggle = true,
  showSectionTitle = true,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const actualMacronutrients = macronutrients || {};
  const expanded = typeof controlledExpanded === 'boolean' ? controlledExpanded : internalExpanded;

  const resolveMacroKey = (key) => LEGACY_FOOD_MACRO_KEY_MAP[key] || key;

  const getMacroConfig = (rawKey) => {
    const normalizedKey = resolveMacroKey(rawKey);
    if (SPECIAL_MACRO_CONFIG[normalizedKey]) {
      return SPECIAL_MACRO_CONFIG[normalizedKey];
    }

    const nutrient = OFF_NUTRIENT_MAP[normalizedKey];
    if (!nutrient) {
      return null;
    }

    return {
      key: normalizedKey,
      label: nutrient.label,
      dataKey: normalizedKey,
      unit: nutrient.unit,
    };
  };

  const getMacroValue = (dataKey) => {
    const value = actualMacronutrients[dataKey];
    if (value === undefined || value === null) return null;
    const parsedValue = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const formatMacroValue = (value, unit) => {
    if (value === null) {
      return '—';
    }

    const formattedValue = formatNumber(value);
    return unit ? `${formattedValue} ${unit}` : formattedValue;
  };

  const uniqueMainMacros = useMemo(() => Array.from(new Set(mainMacros.map(resolveMacroKey))), [mainMacros]);
  const uniqueDisplayedMacros = useMemo(() => Array.from(new Set(displayedMacros.map(resolveMacroKey))), [displayedMacros]);

  const mainMacrosToRender = uniqueMainMacros
    .map((key) => {
      const config = getMacroConfig(key);
      if (!config) return null;
      const value = getMacroValue(config.dataKey);
      return { ...config, value };
    })
    .filter((macro) => !hideUnavailableMacros || macro.value !== null)
    .filter(Boolean);

  const detailMacrosToRender = uniqueDisplayedMacros
    .filter((key) => !uniqueMainMacros.includes(key))
    .map((key) => {
      const config = getMacroConfig(key);
      if (!config) return null;
      const value = getMacroValue(config.dataKey);
      return { ...config, value };
    })
    .filter((macro) => !hideUnavailableMacros || macro.value !== null)
    .filter(Boolean);

  const handleToggle = () => {
    if (typeof onToggle === 'function') {
      onToggle();
      return;
    }

    setInternalExpanded((prev) => !prev);
  };

  return (
    <MacrosContainer>
      {renderMainMacros && mainMacrosToRender.map((macro) => (
        <MacroPill key={macro.key}>
          <MacroLabel>{macro.label}</MacroLabel>
          <MacroAmount>{formatMacroValue(macro.value, macro.unit)}</MacroAmount>
        </MacroPill>
      ))}

      {showToggle && detailMacrosToRender.length > 0 && (
        <NutritionDetailBtn
          className={expanded ? 'active' : ''}
          onClick={handleToggle}
        >
          Détails
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M6.5 9.5 12 15l5.5-5.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </NutritionDetailBtn>
      )}

      {expanded && detailMacrosToRender.length > 0 && (
        <>
          {showSectionTitle && (
            <NutritionSectionTitle>Macros detaillees</NutritionSectionTitle>
          )}
          <NutritionDetails>
            {detailMacrosToRender.map((macro) => (
              <NutritionItem key={macro.key}>
                <NutritionLabel>{macro.label}:</NutritionLabel>
                <NutritionValue $missing={macro.value === null}>
                  {formatMacroValue(macro.value, macro.unit)}
                </NutritionValue>
              </NutritionItem>
            ))}
          </NutritionDetails>
        </>
      )}
    </MacrosContainer>
  );
};

export default FoodMacros;
